import React from 'react';
import { BiArrowBack, BiUser, BiLink } from 'react-icons/bi';
import { Link } from 'react-router-dom';

// Correct Internal Imports
import InputWithLabel from '../components/InputCards/InputWithLabel';
import LoadingSpinner from '../components/Loading/LoadingSpinner';
import VerifyEmail from '../components/VerifyEmail/VerifyEmail';
import { PopupComp, ProfileStatusModal } from '../components';
import useEditProfileFacade from '../facades/useEditProfileFacade';
import { ROUTES } from '../constants';

const EditProfile = () => {
  const {
    user,
    loading,
    formData,
    isDirty,
    btnText,
    showStatusModal,
    setShowStatusModal,
    isUpdating,
    isSuccess,
    handleChange,
    updateProfile,
    setDefaultData,
  } = useEditProfileFacade();

  if (loading && !formData.name) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-100 pb-12">
      {/* Top Bar */}
      <div className="max-w-4xl mx-auto p-4">
        <Link
          to={ROUTES.userProfile(user?._id)}
          className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm hover:bg-blue-50 transition-all font-semibold text-gray-600"
        >
          <BiArrowBack /> Back to Profile
        </Link>
      </div>

      <div className="max-w-4xl mx-auto bg-white m-4 rounded-3xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-8 text-white">
          <h1 className="text-3xl font-black">Account Settings</h1>
          <p className="opacity-80">Update your personal information and social presence</p>
        </div>

        <form onSubmit={updateProfile} className="p-8 space-y-10">
          {/* PERSONAL SECTION */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-2">
              <BiUser className="text-blue-600 mt-1" size={24} />
              <div>
                <h3 className="font-bold text-gray-800">Personal Info</h3>
                <p className="text-xs text-gray-500">How you appear to others.</p>
              </div>
            </div>

            <div className="md:col-span-2 space-y-4">
              <InputWithLabel
                label="Full Name"
                name="name"
                link={formData.name}
                handleChange={handleChange}
              />

              {/* Email + Verify */}
              <div className="space-y-2">
                <InputWithLabel
                  disabled="true"
                  label="Email Address"
                  name="email"
                  link={formData.email}
                  handleChange={handleChange}
                />
                <VerifyEmail email={formData.email} />
              </div>

              {/* ✅ College */}
              <InputWithLabel
                label="College Name"
                name="collegeName"
                link={formData.collegeName}
                handleChange={handleChange}
              />

              {/* Branch + Year */}
              <div className="grid grid-cols-2 gap-4">
                <InputWithLabel
                  label="Branch"
                  name="branch"
                  link={formData.branch}
                  handleChange={handleChange}
                />
                <InputWithLabel
                  label="Graduation Year"
                  name="graduationYear"
                  link={formData.graduationYear}
                  handleChange={handleChange}
                />
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* SOCIAL SECTION */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-2">
              <BiLink className="text-indigo-600 mt-1" size={24} />
              <div>
                <h3 className="font-bold text-gray-800">Professional Links</h3>
                <p className="text-xs text-gray-500">LinkedIn, GitHub & Coding profiles.</p>
              </div>
            </div>

            <div className="md:col-span-2 space-y-4">
              <InputWithLabel
                label="LinkedIn Profile"
                name="linkedinLink"
                link={formData.linkedinLink}
                handleChange={handleChange}
              />
              <InputWithLabel
                label="GitHub Profile"
                name="githubLink"
                link={formData.githubLink}
                handleChange={handleChange}
              />
              <div className="grid grid-cols-2 gap-4">
                <InputWithLabel
                  label="Leetcode"
                  name="leetcodeLink"
                  link={formData.leetcodeLink}
                  handleChange={handleChange}
                />
                <InputWithLabel
                  label="Codeforces"
                  name="codeforcesLink"
                  link={formData.codeforcesLink}
                  handleChange={handleChange}
                />
              </div>

              <InputWithLabel
                label="CodeChef"
                name="codechefLink"
                link={formData.codechefLink}
                handleChange={handleChange}
              />
            </div>
          </div>

          {/* ACTIONS */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-100">
            {isDirty && (
              <button
                type="button"
                onClick={setDefaultData}
                className="px-6 py-3 font-bold text-gray-400 hover:text-gray-600 transition-colors"
              >
                Discard
              </button>
            )}

            <button
              type="submit"
              disabled={!isDirty || btnText === "Updating..."}
              className={`px-10 py-3 rounded-xl font-black transition-all transform active:scale-95 shadow-lg ${
                isDirty
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
              }`}
            >
              {btnText}
            </button>
          </div>
        </form>
      </div>

      {showStatusModal && (
        <PopupComp onClose={() => !isUpdating && setShowStatusModal(false)}>
          <ProfileStatusModal isUpdating={isUpdating} isSuccess={isSuccess} />
        </PopupComp>
      )}
    </div>
  );
};

export default EditProfile;